import * as React from 'react';

import { useTranslation } from 'react-i18next';

import { MainPage } from '../components/MainPage/MainPage';
import { IBeamer } from '../types/beamer.types';
import { AppNavigationProvider } from '../contexts/AppNavigationContext';
import { useUser } from '../hooks/useUser';

import { FeatureFlagServiceContext } from '../contexts/FeatureFlagServiceContext';
import { TenantLicenseProvider } from '../contexts/TenantLicenseContext';
import { TenantSettingsProvider } from '../contexts/TenantSettingsContext';
import { TenantStatusProvider } from '../contexts/TenantStatusContext/TenantStatusContext';
import { useAppLoadingContext } from '../contexts/AppLoadingContext';
import { AuthHandlingWrapper } from './auth0/AuthHandlingWrapper';
import { useAuth0Context } from './auth0/Auth0Context';
import { LicenseManagementProvider } from '../contexts/LicenseManagementContext';
import { IdpContextProvider } from '../contexts/IdentityProviderContext';

export const AppWithAuthenticationInner: React.FC = () => {
    const { i18n } = useTranslation();

    const { loading: authLoading, isAuthenticated } = useAuth0Context();

    const { setLoadingFlag } = useAppLoadingContext();

    React.useEffect(() => {
        setLoadingFlag('auth.init', authLoading);
    }, [authLoading, setLoadingFlag]);

    const user = useUser();

    const beamer: IBeamer = (window as any).Beamer;

    React.useEffect(() => {
        if (!authLoading && user && beamer) {
            beamer.update({
                user_firstname: user.firstName || user.userId,
                user_lastname: user.lastName,
                user_email: user.personIdentifier,
                // sets the badge to be inline with the What's New text
                right: -5,
                top: 7,
                language: i18n.language.substring(0, 2),
            });
            beamer.init();
        }
    }, [user, authLoading, i18n, beamer]);

    // Don't render the app until auth completes, or while actively validating tenant
    if (authLoading) {
        return null;
    }

    return (
        <FeatureFlagServiceContext>
            <TenantLicenseProvider>
                <TenantSettingsProvider>
                    <TenantStatusProvider>
                        <AppNavigationProvider>
                            <LicenseManagementProvider>
                                <IdpContextProvider>
                                    <MainPage />
                                </IdpContextProvider>
                            </LicenseManagementProvider>
                        </AppNavigationProvider>
                    </TenantStatusProvider>
                </TenantSettingsProvider>
            </TenantLicenseProvider>
        </FeatureFlagServiceContext>
    );
};

export const AppWithAuthentication: React.FunctionComponent = () => (
    <AuthHandlingWrapper>
        <AppWithAuthenticationInner />
    </AuthHandlingWrapper>
);
